import api, { ApiResponse } from '.'
import { WorkerCounterTemplateSchema } from '../../../backend/src/services/resources/worker-counter-templates/worker-counter-template.types'
import { EmployerRule } from './employer-rules'

/**
 * Interface representing a worker counter template for API operations
 * Extends the base schema with string ID for frontend usage
 */
export interface WorkerCounterTemplate extends Omit<WorkerCounterTemplateSchema, 'employerRules'> {
  _id: string
  employerRules: EmployerRule[]
}

/**
 * Parameters for fetching worker counter templates
 */
export interface FetchWorkerCounterTemplatesParams {
  _id?: string | string[]
  employer?: string
  filter?: string
}

/**
 * Parameters for creating a new worker counter template
 */
export interface CreateWorkerCounterTemplateParams {
  name: string
  description?: string
  employerRules: string[]
  aggregationMethod: 'sum' | 'max' | 'average' | 'count'
  affectsPeriodSummaryAccounted: boolean
  employer: string
  usagePeriods: {
    startDate: Date
    endDate?: Date
  }[]
}

/**
 * Parameters for updating a worker counter template
 */
export interface UpdateWorkerCounterTemplateParams extends CreateWorkerCounterTemplateParams {
  id: string
}

/**
 * Fetch a list of worker counter templates
 * @param params - Query parameters for filtering templates
 * @returns Promise with API response containing templates array
 */
export const fetch = async (
  params: FetchWorkerCounterTemplatesParams,
): Promise<ApiResponse<WorkerCounterTemplate[]>> => {
  return await api({
    endpoint: '/worker-counter-templates',
    method: 'get',
    params: {
      sort: '-createdAt name',
      populate: ['employer', 'employerRules'],
      ...params,
    },
  })
}

/**
 * Get a single worker counter template by ID
 * @param id - The template ID to retrieve
 * @returns Promise with API response containing the template
 */
export const get = async (id: string): Promise<ApiResponse<WorkerCounterTemplate>> => {
  return await api({
    endpoint: `/worker-counter-templates/${id}`,
    method: 'GET',
    params: {
      populate: ['employer', 'employerRules'],
    },
  })
}

/**
 * Create a new worker counter template
 * @param params - Template creation parameters
 * @returns Promise with API response containing the created template
 */
export const create = async (
  params: CreateWorkerCounterTemplateParams,
): Promise<ApiResponse<WorkerCounterTemplate>> => {
  return await api({
    endpoint: '/worker-counter-templates',
    method: 'post',
    params,
  })
}

/**
 * Update an existing worker counter template
 * @param params - Template update parameters
 * @returns Promise with API response containing the updated template
 */
export const update = async (
  params: UpdateWorkerCounterTemplateParams,
): Promise<ApiResponse<WorkerCounterTemplate>> => {
  const { id, ...rest } = params
  return await api({
    endpoint: `/worker-counter-templates/${id}`,
    method: 'put',
    params: rest,
  })
}
